import React from 'react';
import { ArrowLeft, Github, ExternalLink, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import Portotype2 from "@/assets/achivements/Portotype2.png";

const Automated_Public_Lighting_System: React.FC = () => {
  return (
    <main className="min-h-screen bg-background pt-20">
      {/* Back Navigation */}
      <div className="max-w-6xl mx-auto px-4 pt-16 md:pt-20">
        <Link to="/" className="flex items-center text-muted-foreground hover:text-primary transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Portfolio
        </Link>
      </div>
      
      {/* Hero Section */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col gap-4">
            <Badge variant="secondary" className="w-fit">Featured Project</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">Automated Public Lighting System</h1>
            
            <div className="flex flex-wrap gap-2 my-4">
              <Badge variant="outline" className="bg-white/5 border-primary/20">Arduino</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">IoT</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Solar Panels</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Microcontrollers</Badge>
            </div>
          </div>
        </div>
      </section>
      
      {/* Project Image */}
      <section className="py-6 bg-muted/5">
        <div className="max-w-6xl mx-auto px-4">
          <div className="w-full overflow-hidden rounded-xl border border-border shadow-sm">
            <Carousel
              showThumbs={false}
              showStatus={false}
              infiniteLoop
              autoPlay
              interval={5000}
              className="project-carousel"
            >
              <div>
                <img src={Portotype2} alt="Automated Public Lighting System - Overview" className="w-full object-cover" />
              </div>
              <div>
                <img src="https://via.placeholder.com/1200x600?text=System+Components" alt="System Components" className="w-full object-cover" />
              </div>
              <div>
                <img src="https://via.placeholder.com/1200x600?text=Control+Panel" alt="Control Panel Interface" className="w-full object-cover" />
              </div>
            </Carousel>
          </div>
        </div>
      </section>
      
      {/* Project Details */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Main content */}
            <div className="md:col-span-2 space-y-6">
              <div className="bg-card p-8 rounded-xl border border-border shadow-sm">
                <h2 className="text-2xl font-bold mb-4 text-foreground">Project Overview</h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  An advanced lighting system designed to optimize energy capture and utilization by improving the positioning 
                  of solar panels in public lighting solutions. This project addresses the growing need for sustainable and 
                  efficient public lighting while reducing operational costs and environmental impact.
                </p>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">Development Approach</h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  The system combines IoT technology with solar power optimization to create a smart, automated solution 
                  that adapts to environmental conditions and usage patterns, ensuring maximum efficiency and reliability.
                </p>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">Key Features</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-6">
                  <li>Solar Panel Optimization with automated tracking system</li>
                  <li>Smart Scheduling based on usage patterns</li>
                  <li>Remote Monitoring for real-time system status</li>
                  <li>Intelligent Energy Management and power distribution</li>
                  <li>Battery Management System for optimal performance</li>
                  <li>Environmental Sensors Network for adaptive control</li>
                </ul>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">Technical Implementation</h3>
                <p className="text-muted-foreground leading-relaxed">
                  The system uses Arduino microcontrollers for control, combined with a network of environmental sensors 
                  and IoT connectivity. The solar panel positioning mechanism is controlled by a custom algorithm that 
                  optimizes energy capture throughout the day. The battery management system ensures efficient power 
                  distribution and storage, while the remote monitoring interface provides real-time system status and 
                  performance metrics.
                </p>
              </div>
              
              {/* Project Impact */}
              <div className="bg-card p-8 rounded-xl border border-border shadow-sm">
                <h2 className="text-2xl font-bold mb-4 text-foreground">Project Impact</h2>
                <div className="aspect-video w-full overflow-hidden rounded-lg bg-gradient-to-br from-primary/20 to-purple-600/10 p-8">
                  <h3 className="text-xl font-medium mb-6 text-center">System Performance</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="rounded-md bg-white/10 p-4 text-center">
                      <div className="h-10 w-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-primary/20">
                        <span className="text-primary font-semibold">40%</span>
                      </div>
                      <p className="text-sm font-medium">Energy Savings</p>
                    </div>
                    <div className="rounded-md bg-white/10 p-4 text-center">
                      <div className="h-10 w-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-primary/20">
                        <span className="text-primary font-semibold">24/7</span>
                      </div>
                      <p className="text-sm font-medium">Operation</p>
                    </div>
                    <div className="rounded-md bg-white/10 p-4 text-center">
                      <div className="h-10 w-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-primary/20">
                        <span className="text-primary font-semibold">100%</span>
                      </div>
                      <p className="text-sm font-medium">Reliability</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Project Links</h3>
                <div className="space-y-3">
                  <Button variant="outline" size="lg" className="w-full justify-start" asChild>
                    <a href="https://github.com/Mohan-Balaji/Automated_Public_Lighting" target="_blank" rel="noopener noreferrer" className="flex items-center">
                      <Github className="mr-2 h-5 w-5" />
                      <span>Source Code</span>
                    </a>
                  </Button>
                  <Button variant="outline" size="lg" className="w-full justify-start" asChild>
                    <a href="https://drive.google.com/file/d/13tnQ8V4xNKeEbG_7XKGICjpgIWV9bRcl/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="flex items-center">
                      <ExternalLink className="mr-2 h-5 w-5" />
                      <span>Project Demo</span>
                    </a>
                  </Button>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Technologies Used</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">A</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Arduino</p>
                      <p className="text-sm text-muted-foreground">Microcontroller platform</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">I</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">IoT</p>
                      <p className="text-sm text-muted-foreground">Internet of Things</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">S</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Solar Panels</p>
                      <p className="text-sm text-muted-foreground">Energy collection</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">M</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Microcontrollers</p>
                      <p className="text-sm text-muted-foreground">System control</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Project Outcome</h3>
                <p className="text-muted-foreground mb-4">
                  The system has demonstrated a 40% reduction in energy consumption while maintaining optimal lighting 
                  conditions, making it an ideal solution for sustainable urban infrastructure.
                </p>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-muted-foreground">Energy Savings</span>
                  <span className="text-primary font-medium">40%</span>
                </div>
                <div className="h-2 w-full bg-muted/30 rounded-full overflow-hidden">
                  <div className="h-full w-[40%] bg-gradient-to-r from-primary to-purple-500 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Related Projects */}
      <section className="py-12 bg-muted/5">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl font-bold mb-8 text-foreground">Other Projects You Might Like</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/Dual_Axis_Solar_Tracker" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Dual Axis Solar Tracker</h3>
              <p className="text-muted-foreground line-clamp-2">A system that optimizes solar energy collection by adjusting panels in two axes.</p>
            </Link>
            
            <Link to="/Smart_Irrigation_System_using_Precision_Farming" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Smart Irrigation System</h3>
              <p className="text-muted-foreground line-clamp-2">A system that optimizes water usage in agriculture by monitoring soil moisture levels.</p>
            </Link>
            
            <Link to="/Homelab_Cloud_Storage" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Homelab Cloud Storage</h3>
              <p className="text-muted-foreground line-clamp-2">A self-hosted cloud storage solution using TrueNAS on a repurposed machine.</p>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Automated_Public_Lighting_System;
